import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCohorts } from '@/hooks/useConsoleData';
import { usePhone } from '@/hooks/usePhone';
import { Section } from '@/components/console/Section';
import { BriefSection } from '@/components/console/BriefSection';
import { ActivationPanels } from '@/components/console/ActivationPanels';
import { FirstWeekPanel } from '@/components/console/FirstWeekPanel';
import {
  COHORT_BYS, SORTS, SMALL_N, cents, funnelFor, isSmall, isoDaysAgo, pct, runLedgerNow, sortSources, sourceLabel, weeksFor,
  type CohortBy, type SortKey, type SourceTotal,
} from '@/lib/growth-cohorts';
import { NOT_MEASURED, ago, day, num } from '@/lib/format';
import s from './console.module.css';

/**
 * Growth: attribution to lifetime value. One table per grouping (channel,
 * campaign, promoter, door), each row a source with its funnel, its paid
 * rate, its LTV per signup, its day 7 / day 30 retention, and spend where
 * the marketing worker reported it. Sortable by the columns that decide
 * where to put the next dollar. Every number is a count of rows in the
 * nightly ledger; a thing it could not measure is the words "not measured",
 * and a rate from fewer than ten signups carries its n so nobody reads it
 * as a finding.
 *
 * On a phone (under 640px) the wide table is one card per source with the
 * same numbers, and the filter row scrolls sideways. The desktop table is
 * untouched at 640px and above.
 */

const WINDOWS: { key: string; label: string; days: number | null }[] = [
  { key: '30', label: '30 days', days: 30 },
  { key: '90', label: '90 days', days: 90 },
  { key: 'all', label: 'All time', days: null },
];

function SourceRow({ t, open, onOpen }: { t: SourceTotal; open: boolean; onOpen: () => void }) {
  const small = isSmall(t);
  return (
    <tr className={open ? s.rowOn : undefined} data-source={t.sourceKey}>
      <td>
        <button type="button" className={s.linkBtn} onClick={onOpen}>{sourceLabel(t.sourceKey)}</button>
        {small && <span className={s.muted} style={{ marginLeft: 6, fontSize: '0.74rem' }}>n={t.signups}</span>}
      </td>
      <td style={{ textAlign: 'right' }}>{num(t.signups)}</td>
      <td style={{ textAlign: 'right' }}>{num(t.paid)}</td>
      <td style={{ textAlign: 'right' }}>{pct(t.paidRate)}</td>
      <td style={{ textAlign: 'right' }}>{cents(t.ltvPerSignupCents, t.revenueCurrency)}</td>
      <td style={{ textAlign: 'right' }}>{cents(t.revenueCents, t.revenueCurrency)}</td>
      <td style={{ textAlign: 'right' }}>{pct(t.firstCheckRate)}</td>
      <td style={{ textAlign: 'right' }}>{t.eligibleD7 ? pct(t.retentionD7, t.eligibleD7) : NOT_MEASURED}</td>
      <td style={{ textAlign: 'right' }}>{t.eligibleD30 ? pct(t.retentionD30, t.eligibleD30) : NOT_MEASURED}</td>
      <td style={{ textAlign: 'right' }}>{cents(t.spendCents, t.spendCurrency)}</td>
      <td style={{ textAlign: 'right' }}>{cents(t.costPerSignupCents, t.spendCurrency)}</td>
      <td style={{ textAlign: 'right' }}>{typeof t.valuePerSpendDollar === 'number' ? `${t.valuePerSpendDollar.toFixed(2)}x` : NOT_MEASURED}</td>
    </tr>
  );
}

/** One source as a card, for the phone: the same numbers as the row, two to a line. */
export function SourceCard({ t, open, onOpen }: { t: SourceTotal; open: boolean; onOpen: () => void }) {
  const small = isSmall(t);
  const cell = (label: string, value: string) => (
    <div className={s.pCardNum}><dt>{label}</dt><dd>{value}</dd></div>
  );
  return (
    <article className={`${s.pCard} ${open ? s.personFocus : ''}`} data-source={t.sourceKey}>
      <div className={s.pCardTop}>
        <button type="button" className={`${s.rowLink} ${s.sortBtn}`} style={{ fontSize: '0.98rem', textAlign: 'left' }} onClick={onOpen} aria-expanded={open}>{sourceLabel(t.sourceKey)}</button>
        {small && <span className={s.muted} style={{ fontSize: '0.74rem' }}>n={t.signups}</span>}
      </div>
      <dl className={s.pCardNums}>
        {cell('Signups', num(t.signups))}
        {cell('Paid', `${num(t.paid)} · ${pct(t.paidRate)}`)}
        {cell('LTV / signup', cents(t.ltvPerSignupCents, t.revenueCurrency))}
        {cell('Revenue', cents(t.revenueCents, t.revenueCurrency))}
        {cell('First check', pct(t.firstCheckRate))}
        {cell('Day 7', t.eligibleD7 ? pct(t.retentionD7, t.eligibleD7) : NOT_MEASURED)}
        {cell('Day 30', t.eligibleD30 ? pct(t.retentionD30, t.eligibleD30) : NOT_MEASURED)}
        {cell('Spend', cents(t.spendCents, t.spendCurrency))}
        {cell('Cost / signup', cents(t.costPerSignupCents, t.spendCurrency))}
        {cell('Value / $', typeof t.valuePerSpendDollar === 'number' ? `${t.valuePerSpendDollar.toFixed(2)}x` : NOT_MEASURED)}
      </dl>
      <div className={s.pCardActions}>
        <button type="button" className={s.btnGhost} onClick={onOpen}>{open ? 'Hide the funnel' : 'Show the funnel'}</button>
      </div>
    </article>
  );
}

export default function GrowthPage() {
  const phone = usePhone();
  const [by, setBy] = useState<CohortBy>('channel');
  const [win, setWin] = useState('90');
  const [sort, setSort] = useState<SortKey>('ltvPerSignupCents');
  const [open, setOpen] = useState<string | null>(null);
  const days = WINDOWS.find((w) => w.key === win)?.days ?? null;
  const from = days === null ? undefined : isoDaysAgo(days);
  const q = useCohorts(by, from);
  const qc = useQueryClient();
  const run = useMutation({ mutationFn: runLedgerNow, onSuccess: () => qc.invalidateQueries({ queryKey: ['growth-cohorts'] }) });
  const d = q.data;
  const rows = useMemo(() => (d ? sortSources(d.sources, sort) : []), [d, sort]);
  const opened = rows.find((r) => r.sourceKey === open) ?? null;
  const spendReported = d ? d.spend.length > 0 : false;

  return (
    <div className={s.stack}>
      <div className={s.pageHead}>
        <h1 className={s.pageTitle}>Growth</h1>
        <span className={s.pageSub}>
          {d?.computedAt ? <>ledger computed {ago(d.computedAt)}{typeof d.ageDays === 'number' && d.ageDays >= 2 ? <> · <span className={s.usageAsk}>stale, {d.ageDays} days</span></> : null}</> : 'ledger has not run yet'}
          {' · '}
          <button type="button" className={s.linkBtn} onClick={() => run.mutate()} disabled={run.isPending}>{run.isPending ? 'recomputing, Stripe is being asked per customer' : 'recompute now'}</button>
        </span>
      </div>

      <FirstWeekPanel />

      <BriefSection />

      <ActivationPanels />

      {q.isError && <div className={s.error}>GET /provider/admin/growth/cohorts did not load. Nothing on this page is zero; the ledger is {NOT_MEASURED} right now.</div>}
      {run.isError && <div className={s.error}>Recompute failed: {(run.error as Error)?.message ?? 'no detail'}.</div>}
      {d?.note && <div className={s.notice}>{d.note}</div>}

      <div className={`${s.filters} ${s.scrollRow}`} role="tablist" aria-label="Group cohorts by">
        {COHORT_BYS.map((b) => (
          <button key={b.key} type="button" role="tab" aria-selected={by === b.key} className={`${s.filter} ${by === b.key ? s.filterOn : ''}`} onClick={() => { setBy(b.key); setOpen(null); }}>{b.label}</button>
        ))}
        <span className={s.muted} style={{ marginLeft: 8, fontSize: '0.78rem' }}>signup week</span>
        {WINDOWS.map((w) => (
          <button key={w.key} type="button" className={`${s.filter} ${win === w.key ? s.filterOn : ''}`} onClick={() => setWin(w.key)}>{w.label}</button>
        ))}
        <span className={s.muted} style={{ marginLeft: 8, fontSize: '0.78rem' }}>sort by</span>
        <select className={s.search} style={{ minWidth: 0 }} value={sort} onChange={(e) => setSort(e.target.value as SortKey)} aria-label="Sort sources">
          {SORTS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      </div>

      {d && (
        <div className={s.strip}>
          <div className={s.stat}><span className={s.statLabel}>Sources</span><span className={s.statVal}>{num(d.sources.length)}</span><span className={s.statSub}>by {by}, weeks from {d.window.from ? day(d.window.from) : 'the beginning'}</span></div>
          <div className={s.stat}><span className={s.statLabel}>Signups</span><span className={s.statVal}>{num(d.sources.reduce((n, r) => n + r.signups, 0))}</span><span className={s.statSub}>{num(d.sources.find((r) => r.sourceKey === '(none)')?.signups ?? 0)} with no attribution recorded</span></div>
          <div className={s.stat}><span className={s.statLabel}>Paid</span><span className={s.statVal}>{num(d.sources.reduce((n, r) => n + r.paid, 0))}</span><span className={s.statSub}>real plans; comp and sandbox never count</span></div>
          <div className={s.stat}><span className={s.statLabel}>Spend</span><span className={s.statVal} style={{ fontSize: '1.2rem' }}>{spendReported ? cents(d.spend.reduce((n, r) => n + r.spendCents, 0), d.spend[0].currency) : NOT_MEASURED}</span><span className={s.statSub}>{spendReported ? `${d.spend.length} week rows from the marketing worker` : 'the marketing worker has not reported any spend'}</span></div>
        </div>
      )}

      {phone && (
        <div className={s.people} data-panel="growth-cards">
          {rows.map((t) => <SourceCard key={t.sourceKey} t={t} open={open === t.sourceKey} onOpen={() => setOpen(open === t.sourceKey ? null : t.sourceKey)} />)}
          {d && rows.length === 0 && <div className={s.empty}>No signups in this window with a ledger row yet.</div>}
          {!d && !q.isError && <div className={s.empty}>Loading the ledger.</div>}
        </div>
      )}
      {!phone && <div className={s.card} style={{ padding: 0, overflowX: 'auto' }}>
        <table className={s.table}>
          <thead>
            <tr>
              <th>{COHORT_BYS.find((b) => b.key === by)?.label}</th>
              <th style={{ textAlign: 'right' }}>Signups</th>
              <th style={{ textAlign: 'right' }}>Paid</th>
              <th style={{ textAlign: 'right' }}>Paid rate</th>
              <th style={{ textAlign: 'right' }}>LTV / signup</th>
              <th style={{ textAlign: 'right' }}>Revenue</th>
              <th style={{ textAlign: 'right' }}>First check</th>
              <th style={{ textAlign: 'right' }}>Day 7</th>
              <th style={{ textAlign: 'right' }}>Day 30</th>
              <th style={{ textAlign: 'right' }}>Spend</th>
              <th style={{ textAlign: 'right' }}>Cost / signup</th>
              <th style={{ textAlign: 'right' }}>Value / $</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => <SourceRow key={t.sourceKey} t={t} open={open === t.sourceKey} onOpen={() => setOpen(open === t.sourceKey ? null : t.sourceKey)} />)}
            {d && rows.length === 0 && <tr><td colSpan={12} className={s.empty}>No signups in this window with a ledger row yet.</td></tr>}
            {!d && !q.isError && <tr><td colSpan={12} className={s.empty}>Loading the ledger.</td></tr>}
          </tbody>
        </table>
      </div>}
      <div className={s.muted} style={{ fontSize: '0.78rem' }}>
        Day 7 and day 30 count people whose window has closed (the n after the percentage); a person signed up four days ago is in neither. Revenue is Stripe paid invoices plus RevenueCat gross; a source with one unmeasured paid person shows {NOT_MEASURED} rather than a smaller true number. Rows with n under {SMALL_N} are too small to read a rate from. Spend appears when the marketing worker reports it per campaign and week; value per dollar needs both sides in one currency.
      </div>

      {opened && d && (
        <Section title={`${sourceLabel(opened.sourceKey)}: the funnel`} summary={`${num(opened.signups)} signups over ${opened.weeks} ${opened.weeks === 1 ? 'week' : 'weeks'}`} defaultOpen id="campaign-detail">
          <div className={s.card} style={{ padding: 0, overflowX: 'auto' }}>
            <table className={s.table}>
              <thead><tr><th>Step</th><th style={{ textAlign: 'right' }}>People</th><th style={{ textAlign: 'right' }}>Of signups</th></tr></thead>
              <tbody>
                {funnelFor(opened).map((f) => (
                  <tr key={f.step}><td>{f.step}</td><td style={{ textAlign: 'right' }}>{num(f.count)}</td><td style={{ textAlign: 'right' }}>{pct(f.rate)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={s.groupTitle} style={{ marginTop: 14 }}>By signup week, newest first</div>
          <div className={s.card} style={{ padding: 0, overflowX: 'auto' }}>
            <table className={s.table}>
              <thead><tr><th>Week of</th><th style={{ textAlign: 'right' }}>Signups</th><th style={{ textAlign: 'right' }}>First check</th><th style={{ textAlign: 'right' }}>Day 3</th><th style={{ textAlign: 'right' }}>Habit gate</th><th style={{ textAlign: 'right' }}>Paid</th><th style={{ textAlign: 'right' }}>Revenue</th><th style={{ textAlign: 'right' }}>Day 7</th><th style={{ textAlign: 'right' }}>Day 30</th><th style={{ textAlign: 'right' }}>Spend</th></tr></thead>
              <tbody>
                {weeksFor(d.weeks, opened.sourceKey).map((w) => {
                  const sp = d.spend.filter((x) => x.sourceKey === w.sourceKey && x.weekStart === w.weekStart);
                  return (
                    <tr key={w.weekStart}>
                      <td>{day(w.weekStart)}</td>
                      <td style={{ textAlign: 'right' }}>{num(w.signups)}</td>
                      <td style={{ textAlign: 'right' }}>{num(w.firstCheck)}</td>
                      <td style={{ textAlign: 'right' }}>{num(w.day3)}</td>
                      <td style={{ textAlign: 'right' }}>{num(w.habitGate)}</td>
                      <td style={{ textAlign: 'right' }}>{num(w.paid)}</td>
                      <td style={{ textAlign: 'right' }}>{cents(w.revenueCents, w.revenueCurrency)}</td>
                      <td style={{ textAlign: 'right' }}>{w.eligibleD7 ? pct(w.retentionD7, w.eligibleD7) : NOT_MEASURED}</td>
                      <td style={{ textAlign: 'right' }}>{w.eligibleD30 ? pct(w.retentionD30, w.eligibleD30) : NOT_MEASURED}</td>
                      <td style={{ textAlign: 'right' }}>{sp.length ? cents(sp.reduce((n, x) => n + x.spendCents, 0), sp[0].currency) : NOT_MEASURED}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
      )}
    </div>
  );
}
